import type { RateGrid } from "@redotech/admin-sdk/rpc/schema/rate-tables/rate-grid-schema";
import { rateTableCarrierLabel } from "@redotech/redo-fulfillment-rate-table-common/rate-table-mapping";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import {
  CARRIER_COLORS,
  type ColorMode,
  type MixedModeCell,
  type MixedModeResults,
} from "../types";
import { formatWeightOz } from "../utils/pricing-calculator";
import * as styles from "./comparison-rate-table.module.css";
import { OrderDistributionTable } from "./order-distribution-table";

export enum MixedViewMode {
  Quoted = "Quoted Rate",
  Savings = "Savings",
  Spread = "Spread",
  Orders = "Orders",
  Distribution = "Distribution",
}

interface MixedModeRateTableProps {
  rateGrid: RateGrid;
  mixedResults: MixedModeResults;
  dailyVolume: number;
  colorMode: ColorMode;
  onColorModeChange: (mode: ColorMode) => void;
}

function formatLargeNumber(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(2);
}

function formatCellValue(value: number | null, mode: MixedViewMode): string {
  if (value === null) {
    return "-";
  }
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (mode) {
    case MixedViewMode.Orders:
      return value.toLocaleString();
    case MixedViewMode.Savings:
    case MixedViewMode.Spread:
      if (value === 0) {
        return "$0.00";
      }
      if (value < 0) {
        return `-$${Math.abs(value).toFixed(2)}`;
      }
      return `$${value.toFixed(2)}`;
    default:
      return `$${value.toFixed(2)}`;
  }
}

function getMixedCellValue(
  cell: MixedModeCell,
  mode: MixedViewMode,
): number | null {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (mode) {
    case MixedViewMode.Quoted:
      return cell.quotedRate;
    case MixedViewMode.Savings:
      return cell.savings;
    case MixedViewMode.Spread:
      return cell.spread;
    case MixedViewMode.Orders:
      return cell.orderCount > 0 ? cell.orderCount : null;
    default:
      return null;
  }
}

function getHeatColor(
  value: number,
  min: number,
  max: number,
  mode: MixedViewMode,
): string {
  if (max === min) {
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
    switch (mode) {
      case MixedViewMode.Quoted:
        return "hsl(60, 55%, 82%)";
      case MixedViewMode.Savings:
        return value <= 0 ? "hsl(0, 60%, 85%)" : "hsl(80, 55%, 80%)";
      case MixedViewMode.Spread:
        return value < 0 ? "hsl(0, 60%, 85%)" : "hsl(80, 55%, 80%)";
      case MixedViewMode.Orders:
        return "hsl(210, 55%, 82%)";
      default:
        return "#e5e7eb";
    }
  }

  const ratio = Math.max(0, Math.min(1, (value - min) / (max - min)));
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

  switch (mode) {
    case MixedViewMode.Quoted: {
      const hue = 120 - ratio * 120;
      return `hsl(${hue}, 55%, 82%)`;
    }
    case MixedViewMode.Savings: {
      if (value <= 0) {
        return "hsl(0, 60%, 85%)";
      }
      const hue = 40 + ratio * 80;
      return `hsl(${hue}, 55%, 80%)`;
    }
    case MixedViewMode.Spread: {
      if (value < 0) {
        return "hsl(0, 60%, 85%)";
      }
      if (value === 0) {
        return "hsl(40, 50%, 85%)";
      }
      const hue = 40 + ratio * 80;
      return `hsl(${hue}, 55%, 80%)`;
    }
    case MixedViewMode.Orders: {
      const light = 92 - ratio * 40;
      return `hsl(210, 55%, ${light}%)`;
    }
    default:
      return "#e5e7eb";
  }
}

export const MixedModeRateTable = memo(function MixedModeRateTable({
  rateGrid,
  mixedResults,
  dailyVolume,
  colorMode,
  onColorModeChange,
}: MixedModeRateTableProps) {
  const [viewMode, setViewMode] = useState<MixedViewMode>(
    MixedViewMode.Savings,
  );
  const [useAllOrders, setUseAllOrders] = useState(false);

  const visibleZoneIndices = useMemo(() => {
    const indices: number[] = [];
    for (let zi = 0; zi < rateGrid.zones.length; zi++) {
      const hasData = mixedResults.cells.some(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (row) => row[zi] !== undefined && row[zi]!.quotedRate !== null,
      );
      if (hasData) {
        indices.push(zi);
      }
    }
    return indices;
  }, [rateGrid.zones, mixedResults.cells]);

  const { minVal, maxVal } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const row of mixedResults.cells) {
      for (const cell of row) {
        const value = getMixedCellValue(cell, viewMode);
        if (value !== null && value !== 0) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      }
    }
    if (min === Infinity) {
      min = 0;
    }
    if (max === -Infinity) {
      max = 0;
    }
    return { minVal: min, maxVal: max };
  }, [mixedResults.cells, viewMode]);

  const allOrdersStats = useMemo(() => {
    let totalSpread = 0;
    let totalOrders = 0;
    for (const row of mixedResults.cells) {
      for (const cell of row) {
        if (cell.orderCount === 0 || cell.spread === null) {
          continue;
        }
        totalSpread += cell.spread * cell.orderCount;
        totalOrders += cell.orderCount;
      }
    }
    const scaleFactor =
      totalOrders > 0 ? mixedResults.summary.annualOrders / totalOrders : 0;
    return {
      annualSpread: totalSpread * scaleFactor,
      avgSpreadPerOrder: totalOrders > 0 ? totalSpread / totalOrders : 0,
    };
  }, [mixedResults.cells, mixedResults.summary.annualOrders]);

  const { summary, carrierBreakdown } = mixedResults;

  return (
    <Flex dir="column" gap="md">
      <Flex dir="column" gap="sm">
        <Flex gap="md" wrap="wrap">
          <StatCard
            label="Annual Volume"
            value={summary.annualOrders.toLocaleString()}
          />
          <StatCard
            label="Orders Won"
            textColor={summary.ordersWonPct > 50 ? "success" : "warning"}
            value={`${summary.ordersWonPct.toFixed(1)}%`}
          />
          <StatCard
            label="Annual Merchant Savings"
            textColor="success"
            value={`$${formatLargeNumber(summary.annualSavings)}`}
          />
          <StatCard
            label="Annual Redo Revenue"
            textColor="primary"
            value={`$${formatLargeNumber(useAllOrders ? allOrdersStats.annualSpread : summary.annualSpread)}`}
          />
          <StatCard
            label="Avg Spread/Order"
            textColor="primary"
            value={`$${(useAllOrders ? allOrdersStats.avgSpreadPerOrder : summary.avgSpreadPerOrder).toFixed(2)}`}
          />
        </Flex>
        <Flex gap="xs">
          <ViewModeButton
            active={!useAllOrders}
            label="Won orders"
            onClick={() => setUseAllOrders(false)}
          />
          <ViewModeButton
            active={useAllOrders}
            label="All orders"
            onClick={() => setUseAllOrders(true)}
          />
        </Flex>
      </Flex>

      {carrierBreakdown.length > 0 && (
        <Flex gap="sm" wrap="wrap">
          {carrierBreakdown.map((bd) => (
            <Flex
              align="center"
              borderRadius="md"
              gap="xs"
              key={bd.carrier}
              px="sm"
              py="xs"
              style={{
                backgroundColor: CARRIER_COLORS[bd.carrier],
                border: "1px solid rgba(0,0,0,0.1)",
              }}
            >
              <Text fontSize="xs" fontWeight="semibold">
                {rateTableCarrierLabel(bd.carrier)}
              </Text>
              <Text fontSize="xs">
                {bd.ordersPct.toFixed(0)}% ({bd.ordersWon.toLocaleString()}{" "}
                orders)
              </Text>
            </Flex>
          ))}
        </Flex>
      )}

      <Flex align="center" gap="md" justify="space-between">
        <Flex gap="xs" wrap="wrap">
          {Object.values(MixedViewMode).map((mode) => (
            <ViewModeButton
              active={viewMode === mode}
              key={mode}
              label={mode}
              onClick={() => setViewMode(mode)}
            />
          ))}
        </Flex>
        <Flex gap="xs">
          <ColorModeToggle
            active={colorMode === "carrier"}
            label="Carrier"
            onClick={() => onColorModeChange("carrier")}
          />
          <ColorModeToggle
            active={colorMode === "heatmap"}
            label="Heatmap"
            onClick={() => onColorModeChange("heatmap")}
          />
        </Flex>
      </Flex>

      {viewMode === MixedViewMode.Distribution ? (
        <OrderDistributionTable rateGrid={rateGrid} />
      ) : (
        <Flex
          bgColor="secondary"
          borderColor="primary"
          borderRadius="lg"
          borderWidth="1px"
          className={styles.tableContainer}
          overflow="auto"
        >
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.tableHeaderWeight}>Weight</th>
                {visibleZoneIndices.map((zi) => (
                  <th className={styles.tableHeaderZone} key={zi}>
                    Z{rateGrid.zones[zi]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mixedResults.cells.map((row, wi) => (
                <tr key={wi}>
                  <td className={styles.tableRowHeader}>
                    {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                    {formatWeightOz(rateGrid.weightBucketsOz[wi]!)}
                  </td>
                  {visibleZoneIndices.map((zi) => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const cell = row[zi]!;
                    const value = getMixedCellValue(cell, viewMode);

                    let bgColor: string;
                    if (
                      colorMode === "carrier" &&
                      cell.winningCarrier &&
                      value !== null
                    ) {
                      bgColor = CARRIER_COLORS[cell.winningCarrier];
                    } else if (value !== null) {
                      bgColor = getHeatColor(value, minVal, maxVal, viewMode);
                    } else {
                      bgColor = "#f3f4f6";
                    }

                    const cellClassName = [
                      styles.tableCellDefault,
                      value !== null
                        ? styles.tableCellWithValue
                        : styles.tableCellEmpty,
                    ].join(" ");

                    const winnerLabel = cell.winningCarrier
                      ? rateTableCarrierLabel(cell.winningCarrier)
                      : "None";

                    return (
                      <td
                        className={cellClassName}
                        key={zi}
                        style={{ backgroundColor: bgColor }}
                        title={`Orders: ${cell.orderCount}\nMerchant: $${cell.merchantRate?.toFixed(2) || "-"}\nWinner: ${winnerLabel}\nCarrier Rate: $${cell.carrierRate?.toFixed(2) || "-"}\nQuoted: $${cell.quotedRate?.toFixed(2) || "-"}\nSavings: $${cell.savings?.toFixed(2) || "-"}\nSpread: $${cell.spread?.toFixed(2) || "-"}`}
                      >
                        {formatCellValue(value, viewMode)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Flex>
      )}
    </Flex>
  );
});

const StatCard = memo(function StatCard({
  label,
  value,
  textColor = "primary",
}: {
  label: string;
  value: string;
  textColor?: "primary" | "success" | "warning";
}) {
  return (
    <Flex
      bgColor="secondary"
      borderColor="primary"
      borderRadius="lg"
      borderWidth="1px"
      className={styles.statCard}
      dir="column"
      gap="xs"
      p="sm"
    >
      <Text fontSize="xs" textColor="secondary">
        {label}
      </Text>
      <Text
        fontSize="md"
        fontWeight="semibold"
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        textColor={textColor as "primary"}
      >
        {value}
      </Text>
    </Flex>
  );
});

const ViewModeButton = memo(function ViewModeButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      borderRadius="md"
      className={
        active ? styles.viewModeButtonActive : styles.viewModeButtonInactive
      }
      onClick={onClick}
      p="xs"
      px="sm"
    >
      <Text
        fontSize="xs"
        fontWeight={active ? "semibold" : "regular"}
        textColor={active ? "primary" : "secondary"}
      >
        {label}
      </Text>
    </Flex>
  );
});

const ColorModeToggle = memo(function ColorModeToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      borderRadius="md"
      className={
        active ? styles.viewModeButtonActive : styles.viewModeButtonInactive
      }
      onClick={onClick}
      p="xs"
      px="sm"
    >
      <Text
        fontSize="xs"
        fontWeight={active ? "semibold" : "regular"}
        textColor={active ? "primary" : "secondary"}
      >
        {label}
      </Text>
    </Flex>
  );
});
